/**
 * `<Toaster/>` — неблокирующие уведомления Earth Tech DS.
 *
 * Монтируется ОДИН раз в `App` (см. `src/App.tsx`), рисуется порталом в
 * `document.body`, чтобы не зависеть от `overflow:hidden` каркаса и не попадать
 * под трансформации карты.
 *
 * Почему свой компонент, а не библиотека: в зависимостях нет ни `sonner`, ни
 * `@radix-ui/react-toast`, а поведение тут на полсотни строк — тянуть новую
 * зависимость в бандл ради этого незачем (правило bundle-size, Head-Web).
 * Паттерн — sonner: стопка снизу справа, карточки на токенах DS, автоскрытие
 * для успеха, ручное закрытие для ошибок.
 *
 * Раскладка: снизу справа НАД ФАБом «ИИ» (он `fixed` 56px + 24px отступ,
 * `z-40`) — иначе тост накрывал бы кнопку. Отступ считается через `env(
 * safe-area-inset-*)`, чтобы на мобиле не залезать под системную полосу.
 *
 * Второй контейнер — ЦЕНТР экрана — только для итогов массовых действий
 * (`toastBulkResult`, AK-721): «заполнено полей 22 · 2 407 га» человек ждёт
 * там, куда смотрит, а не в углу за таблицей. Контейнер не ловит клики
 * (`pointer-events-none`), кликабельна только сама карточка — таблица под
 * ним остаётся рабочей.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSyncExternalStore } from 'react';
import { Check, Info, TriangleAlert, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  dismissToast,
  getToasts,
  subscribeToasts,
  toastAutoDismissMs,
  type ToastItem,
  type ToastKind,
} from '@/lib/toast';

const ICONS: Record<ToastKind, typeof Check> = {
  success: Check,
  error: TriangleAlert,
  warning: TriangleAlert,
  info: Info,
};

/** Акцент карточки: цветом кодируется смысл, фон всегда карточный (читаемость). */
const ACCENT: Record<ToastKind, string> = {
  success: 'border-l-primary',
  error: 'border-l-destructive',
  warning: 'border-l-accent-brand',
  info: 'border-l-border',
};

const ICON_TONE: Record<ToastKind, string> = {
  success: 'text-primary',
  error: 'text-destructive',
  warning: 'text-accent-brand',
  info: 'text-muted-foreground',
};

const TITLE: Record<ToastKind, string> = {
  success: 'Готово',
  error: 'Ошибка',
  warning: 'Внимание',
  info: 'Сообщение',
};

function ToastRow({ item }: { item: ToastItem }) {
  const { id, kind, message, sticky, placement } = item;
  const Icon = ICONS[kind];

  useEffect(() => {
    const ms = toastAutoDismissMs(kind, placement);
    if (ms == null) return;
    const timer = window.setTimeout(() => dismissToast(id), ms);
    return () => window.clearTimeout(timer);
  }, [id, kind, placement]);

  return (
    <div
      data-testid="toast"
      data-toast-kind={kind}
      data-toast-placement={placement}
      // Ошибка обязана быть озвучена немедленно, успех — не перебивая.
      role={kind === 'error' || kind === 'warning' ? 'alert' : 'status'}
      aria-live={kind === 'error' || kind === 'warning' ? 'assertive' : 'polite'}
      className={cn(
        'pointer-events-auto flex w-full items-start gap-3 rounded-lg border border-l-4',
        'border-border bg-card p-3 text-sm text-card-foreground shadow-lg',
        // Итог по центру — крупнее и заметнее: это ответ на нажатие, а не фон.
        placement === 'center' && 'p-4 text-base shadow-xl',
        ACCENT[kind],
      )}
    >
      <Icon size={16} aria-hidden className={cn('mt-1 shrink-0', ICON_TONE[kind])} />
      <div className="min-w-0 flex-1">
        <span className="sr-only">{TITLE[kind]}: </span>
        <span className="block break-words leading-6">{message}</span>
      </div>
      <button
        type="button"
        onClick={() => dismissToast(id)}
        aria-label={sticky ? 'Закрыть уведомление' : 'Скрыть уведомление'}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-accent hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  );
}

export function Toaster() {
  const items = useSyncExternalStore(subscribeToasts, getToasts, getToasts);

  if (typeof document === 'undefined') return null;

  const stack = items.filter((t) => t.placement !== 'center');
  const center = items.filter((t) => t.placement === 'center');

  return createPortal(
    <>
      <div
        data-testid="toaster"
        className="pointer-events-none fixed z-50 flex w-full max-w-sm flex-col gap-2"
        style={{
          // ФАБ «ИИ»: 24px отступ + 56px кнопка + 12px зазор.
          bottom: 'calc(5.75rem + env(safe-area-inset-bottom, 0px))',
          right: 'calc(1.5rem + env(safe-area-inset-right, 0px))',
          maxWidth: 'min(24rem, calc(100vw - 3rem))',
        }}
      >
        {stack.map((item) => (
          <ToastRow key={item.id} item={item} />
        ))}
      </div>
      {center.length > 0 && (
        <div
          data-testid="toaster-center"
          // `inset-0` + flex-центр — от viewport, без хардкода высот (правило
          // адаптивной вёрстки): баннеры сверху и панели сбоку центр не ломают.
          className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-6"
        >
          <div
            className="flex w-full flex-col gap-2"
            style={{ maxWidth: 'min(28rem, calc(100vw - 3rem))' }}
          >
            {center.map((item) => (
              <ToastRow key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}

export default Toaster;
